import { useEffect, useState, useMemo } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import CatalogServiceModal from 'pages/Modals/CatalogServiceModal'
import ItemTabs from 'components/ItemTabs'
import CatalogServiceList from 'components/Lists/CatalogServiceList'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaPlus } from 'react-icons/fa'
import { Container } from '../../styles'

export default function Catalog ({
  businessId,
  requestConfig
}) {
  const [loading, setLoading] = useState (false)
  const [catalog, setCatalog] = useState ({})
  const [selectedCategory, setSelectedCategory] = useState (null)
  const [showAddCatalogServiceModal, setShowAddCatalogServiceModal] = useState (false)
  /*
    `business_config.catalog`, no singular, é o area code cadastrado na tabela `permissions`
    — conferido em produção. No plural nunca casava, então a permissão de edição era negada
    em silêncio para profissional. Salão, empresa e admin não sentiam: o `hasPermission`
    devolve `true` para eles antes de olhar a lista.
  */
  const hasEditPermission = AuthService.hasPermission('business_config.catalog', 'edit')

  const categoryTabs = useMemo (() => {
    if (!catalog.categories) return []

    return catalog.categories
      .sort ((a, b) => a.name.localeCompare (b.name, 'pt', { sensitivity: 'base' }))
      .map (category => ({
        label: category.name,
        value: category.id
      }))
  }, [catalog])

  const filteredServices = useMemo (() => {
    if (!catalog.categories || !selectedCategory) return []
    const category = catalog.categories.find (cat => cat.id === selectedCategory)
    return category ? category.services : []
  }, [catalog, selectedCategory])

  useEffect (() => {
    getCatalog ()
  }, [])

  useEffect (() => {
    if (catalog.categories && catalog.categories.length > 0 && !selectedCategory) {
      setSelectedCategory (catalog.categories[0].id)
    }
  }, [catalog])

  const getCatalog = async () => {
    try {
      setLoading (true)

      const { data: catalog } = await cutileiApi.get (
        `/businesses/${businessId}/catalog`, requestConfig
      )

      setCatalog (catalog)
    } catch (error) {
      console.error (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleAddCatalogServiceModal = () => setShowAddCatalogServiceModal (prev => !prev)

  const handleServiceAdded = newService => {
    setCatalog (prev => {
      const updatedCatalog = { ...prev }
      const catIdx = updatedCatalog.categories.findIndex (cat => cat.id === newService.category_id)

      if (catIdx !== -1) {
        updatedCatalog.categories[catIdx].services.push (newService)
      } else {
        updatedCatalog.categories.push ({
          id: newService.category_id,
          name: newService.category_name,
          services: [newService]
        })

        setSelectedCategory (newService.category_id)
      }

      return updatedCatalog
    })
  }

  const handleEditService = service => {
    setCatalog (prev => {
      const updatedCatalog = { ...prev }
      const catIdx = updatedCatalog.categories.findIndex (cat => cat.id === service.category_id)

      if (catIdx !== -1) {
        const serviceIndex = updatedCatalog.categories[catIdx].services.findIndex (s => (
          s.id === service.id
        ))

        if (serviceIndex !== -1) {
          updatedCatalog.categories[catIdx].services[serviceIndex] = service
        }
      } else {
        updatedCatalog.categories.push ({
          id: service.category_id,
          name: service.category_name,
          services: [service]
        })

        setSelectedCategory (service.category_id)
      }

      return updatedCatalog
    })
  }

  const handleDeleteService = service => {
    setCatalog (prev => {
      const updatedCatalog = { ...prev }
      const catIdx = updatedCatalog.categories.findIndex (cat => cat.id === selectedCategory)

      if (catIdx !== -1) {
        updatedCatalog.categories[catIdx].services = updatedCatalog.categories[catIdx].services.filter (s => (
          s.id !== service.id
        ))
      }

      if (updatedCatalog.categories[catIdx].services.length === 0) {
        updatedCatalog.categories.splice (catIdx, 1)
      }

      setSelectedCategory (prev => {
        if (prev === selectedCategory) {
          if (updatedCatalog.categories.length > 0) return updatedCatalog.categories[0].id
          else return null
        }
        return prev
      })

      return updatedCatalog
    })
  }

  if (loading) return <Loading style={{marginTop: 20}}/>

  return (
    <Container style={{paddingTop: 0}}>
      {hasEditPermission && (
        <SecondaryButton
          label='Adicionar serviço'
          width={160}
          leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 2}}/>}
          onClick={toggleAddCatalogServiceModal}
        />
      )}
      {categoryTabs.length > 0 && (
        <ItemTabs
          tabs={categoryTabs}
          selectedTab={selectedCategory}
          tabWidth={180}
          onTabChange={setSelectedCategory}
        />
      )}
      <CatalogServiceList
        services={filteredServices}
        catalogID={catalog.id}
        onEdit={handleEditService}
        onDelete={handleDeleteService}
      />
      <CatalogServiceModal
        visible={showAddCatalogServiceModal}
        catalogID={catalog.id}
        onConfirm={handleServiceAdded}
        onClose={toggleAddCatalogServiceModal}
      />
    </Container>
  )
}
